import { Device } from "@fairfleet/geotab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { MaintenancePriorityCode } from "@/constants/MaintenancePriority";
import { MAINTENANCE_SOURCE } from "@/constants/MaintenanceSource";
import { WarningType } from "@/models/Warning";

export interface CreatePmOrderInput {
  device: Device;
  ruleId: string;
  pmTitle: string;
  pmDescription: string;
  warningType: WarningType;
  priorityCode: MaintenancePriorityCode;
  description: string;
  notes: string;
}

export interface CreatePmOrderResult {
  orderId: string;
  jobId: string;
}

export class CreatePmOrderError extends Error {
  stage: "order" | "job";
  orderId?: string;
  cause?: unknown;
  constructor(stage: "order" | "job", message: string, cause?: unknown, orderId?: string) {
    super(message);
    this.stage = stage;
    this.cause = cause;
    this.orderId = orderId;
  }
}

const SEVERITY_BY_WARNING: Partial<Record<WarningType, number>> = {
  [WarningType.PM]: 200,
  [WarningType.PM1]: 400,
  [WarningType.TT_YELLOW]: 100,
  [WarningType.TT_RED]: 300,
};

function severityFor(warningType: WarningType): number {
  const severity = SEVERITY_BY_WARNING[warningType];
  if (severity === undefined) {
    throw new CreatePmOrderError(
      "order",
      `No severity mapping for warning type: ${warningType}`,
    );
  }
  return severity;
}

export function useCreateMaintenanceWorkOrder() {
  const { geotabClient, me } = useGeotabClient();
  const queryClient = useQueryClient();

  return useMutation<CreatePmOrderResult, CreatePmOrderError, CreatePmOrderInput>({
    mutationFn: async (input): Promise<CreatePmOrderResult> => {
      if (!me) {
        throw new CreatePmOrderError("order", "No authenticated user");
      }
      const severityCode = severityFor(input.warningType);

      const deviceRef = { id: input.device.id };

      const pmNotePrefix = `[PM:${input.ruleId}]`;
      const orderNotes = input.notes ? `${pmNotePrefix} ${input.notes}` : pmNotePrefix;

      const orderEntity = {
        device: deviceRef,
        statusCode: 1,
        priorityCode: input.priorityCode,
        notes: orderNotes,
        costCurrency: "Eur",
        currencyConversionFailed: false,
        mediaFiles: [],
        odometerReading: "0000000000000000",
        engineHoursReading: "00:00:00",
        engineHoursReadingInHours: 0,
      };

      let orderId: string;
      try {
        orderId = (await geotabClient.call("Add", {
          typeName: "MaintenanceWorkOrder",
          entity: orderEntity,
        })) as string;
      } catch (e) {
        console.error("Add MaintenanceWorkOrder failed", { entity: orderEntity, error: e });
        throw new CreatePmOrderError("order", "Add MaintenanceWorkOrder failed", e);
      }

      const maintenanceTypeEntity = {
        name: input.pmTitle,
        customSource: MAINTENANCE_SOURCE.ECO_PM,
        labels: ["fairfleet:predictive-maintenance"],
      };

      let maintenanceTypeId: string;
      try {
        maintenanceTypeId = (await geotabClient.call("Add", {
          typeName: "EventType",
          entity: maintenanceTypeEntity,
        })) as string;
      } catch (e) {
        console.error("Add EventType (MaintenanceType) failed", {
          entity: maintenanceTypeEntity,
          error: e,
        });
        throw new CreatePmOrderError("job", "Add MaintenanceType failed", e, orderId);
      }

      const jobEntity = {
        workOrder: { id: orderId },
        device: deviceRef,
        maintenanceType: { id: maintenanceTypeId },
        statusCode: 1,
        severityCode,
        description: input.description,
        notes: orderNotes,
      };

      let jobId: string;
      try {
        jobId = (await geotabClient.call("Add", {
          typeName: "MaintenanceWorkOrderJob",
          entity: jobEntity,
        })) as string;
      } catch (e) {
        console.error("Add MaintenanceWorkOrderJob failed", {
          entity: jobEntity,
          orderId,
          error: e,
        });
        throw new CreatePmOrderError("job", "Add MaintenanceWorkOrderJob failed", e, orderId);
      }

      return { orderId, jobId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pmJobsByDevice"] });
      queryClient.invalidateQueries({ queryKey: ["maintenanceWorkOrderJob"] });
      queryClient.invalidateQueries({ queryKey: ["maintenanceWorkOrder"] });
    },
  });
}
